import { Trans, useLingui } from "@lingui/react/macro";
import { updateDescription } from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import TextArea from "@/components/TextArea/TextArea";

export const EditDescription = ({
  currentDescription,
  onSuccess,
  activityId,
}: {
  currentDescription: string | null | undefined;
  onSuccess: () => void;
  activityId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      description: currentDescription ?? "",
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (description: string) =>
      updateDescription(activityId, description),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.description);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar beskrivning`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <TextArea
              label={t`Beskrivning`}
              description={t`Visas inte för patienten`}
              showOptionalLabel
              errorMessage={errors.description?.message}
              {...register("description")}
            />
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : (
            <></>
          )}
        </>
      )}
    </Form>
  );
};
